import axios, { AxiosInstance } from 'axios';

const BASE_URL = process.env.UMI_APP_API_URL;

class AdminService {
  private api: AxiosInstance;
  
  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
    });

    // Interceptor para agregar el token en cada request
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      // Only add Authorization from localStorage when it's not already provided in the request config
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('AdminService: No token found in localStorage');
        }
      }
      return config;
    });

    // Interceptor para manejar errores de respuesta
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
        }
        return Promise.reject(error);
      }
    );
  }

  // Funcion para manejar respuestas (lanza error si no es 200)
  private handleResponse(resp: any) {
    if (resp.data.status !== 200) {
      throw new Error(resp.data.message || 'Error inesperado');
    }
    return resp.data.data || resp.data;
  }

  // ===== USUARIOS =====
  async getUsers() { return this.handleResponse(await this.api.get('/user')); }
  async getUser(id: number) { return this.handleResponse(await this.api.get(`/user/${id}`)); }
  async createUser(data: any) {
    // Se usa el token de sesión del usuario que está creando la cuenta, vía el
    // interceptor de arriba. Antes se sobreescribía con UMI_APP_API_TOKEN, un
    // token estático compilado en el bundle: cualquiera que lo extrajera podía
    // dar de alta usuarios. La ruta exige ahora sesión real (isAuthenticated).
    return this.handleResponse(await this.api.post('/user', data));
  }
  /**
   * Cambia la contraseña del usuario de la sesión. El servidor ignora cualquier
   * identificador que se le mande: siempre opera sobre req.currentUser.
   */
  async changeOwnPassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword?: string;
  }) {
    return this.handleResponse(await this.api.patch('/user/change-password', data));
  }

  /**
   * Marca a los usuarios para que definan una contraseña nueva en su próximo
   * ingreso. Sin `userIds`, aplica a todos los activos. Solo administradores;
   * el servidor lo comprueba, no basta con ocultar el botón.
   */
  async forcePasswordChange(userIds?: number[]) {
    const body = userIds && userIds.length ? { userIds } : {};
    return this.handleResponse(await this.api.post('/user/force-password-change', body));
  }

  async updateUser(id: number, data: any) { return this.handleResponse(await this.api.patch(`/user/${id}`, data)); }
  async deleteUser(id: number) { return this.handleResponse(await this.api.delete(`/user/${id}`)); }

  // ===== ROLES =====
  async getRoles() { return this.handleResponse(await this.api.get('/rol')); }
  async getRol(id: number) { return this.handleResponse(await this.api.get(`/rol/${id}`)); }
  async createRol(data: any) {
    // Igual que createUser: token de sesión vía interceptor, no el estático.
    return this.handleResponse(await this.api.post('/rol', data));
  }
  async updateRol(id: number, data: any) { return this.handleResponse(await this.api.patch(`/rol/${id}`, data)); }
  async deleteRol(id: number) { return this.handleResponse(await this.api.delete(`/rol/${id}`)); }

  // ===== PERMISOS =====
  async getPermissions(filters?: { fk_rol?: number; fk_module?: number }) {
    try {
      const resp = await this.api.get('/permission', { params: filters });
      return this.handleResponse(resp);
    } catch (e: any) {
      // Backend returns 404 when a filtered list has no matches. Treat as empty list for UIs.
      const status = e?.response?.status ?? e?.response?.data?.status;
      if (status === 404) return [];
      throw e;
    }
  }
  async getPermission(id: number) { return this.handleResponse(await this.api.get(`/permission/${id}`)); }
  async createPermission(data: any) { return this.handleResponse(await this.api.post('/permission', data)); }
  async updatePermission(id: number, data: any) { return this.handleResponse(await this.api.patch(`/permission/${id}`, data)); }
  async deletePermission(id: number) { return this.handleResponse(await this.api.delete(`/permission/${id}`)); }
  async getPermissionsByRole(roleId: number) { return this.handleResponse(await this.api.get(`/permission-rol/${roleId}`)); }

  // ===== OBTENER MODULOS ACCESIBLES POR ROL =====
  async getAccessibleModulesByRole(roleId: number) {
    const response = await this.getPermissionsByRole(roleId);

    // Extraer modulos unicos de los permisos
    const accessibleModules = new Set();

    if (response?.data && Array.isArray(response.data)) {
      response.data.forEach((permission: any) => {
        if (permission.module && (permission.C || permission.R || permission.U || permission.D)) {
          accessibleModules.add({
            name: permission.module,
            hasCreate: permission.C,
            hasRead: permission.R,
            hasUpdate: permission.U,
            hasDelete: permission.D,
          });
        }
      });
    }

    return {
      ...response,
      modules: Array.from(accessibleModules),
    };
  }

  // ===== MODULOS =====
  async getModules() { return this.handleResponse(await this.api.get('/module')); }
  async getModule(id: number) { return this.handleResponse(await this.api.get(`/module/${id}`)); }
  async createModule(data: any) { return this.handleResponse(await this.api.post('/module', data)); }
  async updateModule(id: number, data: any) { return this.handleResponse(await this.api.patch(`/module/${id}`, data)); }
  async deleteModule(id: number) { return this.handleResponse(await this.api.delete(`/module/${id}`)); }

  // ===== SEGMENTOS =====
  async getSegments() { return this.handleResponse(await this.api.get('/segment')); }
  async getSegment(id: number) { return this.handleResponse(await this.api.get(`/segment/${id}`)); }
  async createSegment(data: any) { return this.handleResponse(await this.api.post('/segment', data)); }
  async updateSegment(id: number, data: any) { return this.handleResponse(await this.api.patch(`/segment/${id}`, data)); }
  async deleteSegment(id: number) { return this.handleResponse(await this.api.delete(`/segment/${id}`)); }

   // Financial Budget Types CRUD
  async getFinancialBudgetTypes() {
    return this.handleResponse(await this.api.get("/financial-budget-type"));
  }
  async createFinancialBudgetType(payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.post("/financial-budget-type", payload));
  }
  async updateFinancialBudgetType(id: number, payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.patch(`/financial-budget-type/${id}`, payload));
  }
  async deleteFinancialBudgetType(id: number) {
    return this.handleResponse(await this.api.delete(`/financial-budget-type/${id}`));
  }

  // Money Types CRUD (tipos de dinero)
  async getMoneyTypes() {
    return this.handleResponse(await this.api.get("/type-money"));
  }
  async createMoneyType(payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.post("/type-money", payload));
  }
  async updateMoneyType(id: number, payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.patch(`/type-money/${id}`, payload));
  }
  async deleteMoneyType(id: number) {
    return this.handleResponse(await this.api.delete(`/type-money/${id}`));
  }

  // Project Date Types CRUD (asumiendo endpoint /project-date-type)
  async getProjectDateTypes() {
    return this.handleResponse(await this.api.get("/type-date"));
  }
  async createProjectDateType(payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.post("/type-date", payload));
  }
  async updateProjectDateType(id: number, payload: { name: string; description?: string }) {
    return this.handleResponse(await this.api.patch(`/type-date/${id}`, payload));
  }
  async deleteProjectDateType(id: number) {
    return this.handleResponse(await this.api.delete(`/type-date/${id}`));
  }


  // Actions CRUD (independiente, no ligado a proyecto)

   async getActions(params?: { includeInactive?: boolean; active?: boolean }) {
    const query: any = {};
    if (params?.includeInactive) query.include_inactive = true;
    if (params?.active !== undefined) query.active = params.active;
    return this.handleResponse(await this.api.get("/type-action", { params: query }));
  }
  async createAction(payload: { name: string; description?: string; active?: boolean; funnel_stage?: string | null }) {
    return this.handleResponse(await this.api.post("/type-action", payload));
  }
  async updateAction(id: number, payload: { name?: string; description?: string; active?: boolean; funnel_stage?: string | null }) {
    return this.handleResponse(await this.api.patch(`/type-action/${id}`, payload));
  }
  async deleteAction(id: number) {
    return this.handleResponse(await this.api.delete(`/type-action/${id}`));
  }

  // Trigger a financial data update. Returns raw axios response because
  // the endpoint can take several seconds and may not follow the usual
  // { status, data } wrapper used elsewhere.
  async triggerFinancialDataUpdate(payload: any = {}) {
    // increase timeout to 30s to accommodate long-running task
    return await this.api.post('/financial-data', payload, { timeout: 30000 });
  }

  // ===== METODOS (MIGRACION) =====
  async migrationUpdateClientStatuses(payload: { months?: number; dryRun?: boolean } = {}) {
    return this.handleResponse(await this.api.post('/admin/methods/client-statuses', payload));
  }

  async migrationResetClientPositions(payload: { gap?: number; dryRun?: boolean } = {}) {
    return this.handleResponse(await this.api.post('/admin/methods/reset-client-positions', payload));
  }

  async activateClientsKanbanActive(payload: { months?: number; dryRun?: boolean } = {}) {
    return this.handleResponse(
      await this.api.post('/admin/methods/activate-clients-kanban-active', payload)
    );
  }

  async subscribeToSellerProjects(payload: {
    seller_user_id: number;
    notify_actions?: boolean;
    notify_comments?: boolean;
    include_existing_projects?: boolean;
  }) {
    return this.handleResponse(
      await this.api.post('/admin/methods/project-subscription/by-seller', payload)
    );
  }

  // ===== SMTP / NOTIFICATIONS =====
  async getSmtpConfig() {
    return this.handleResponse(await this.api.get('/notification/smtp'));
  }
  async updateSmtpConfig(payload: any) {
    return this.handleResponse(await this.api.patch('/notification/smtp', payload));
  }
  async testSmtpConnection() {
    return this.handleResponse(await this.api.post('/notification/smtp/test-connection', {}));
  }
  async testSmtpEmail(payload: { to?: string } = {}) {
    return this.handleResponse(await this.api.post('/notification/smtp/test-email', payload));
  }

  // ===== EMAIL TEMPLATES =====
  async getEmailTemplates() {
    return this.handleResponse(await this.api.get('/email-template'));
  }
  async getEmailTemplate(id: number) {
    return this.handleResponse(await this.api.get(`/email-template/${id}`));
  }
  async updateEmailTemplate(id: number, payload: { subject?: string; html_body?: string; text_body?: string; active?: boolean }) {
    return this.handleResponse(await this.api.patch(`/email-template/${id}`, payload));
  }
  async previewEmailTemplate(id: number, useDefault = false) {
    return this.handleResponse(await this.api.post(`/email-template/${id}/preview`, { useDefault }));
  }
  async resetEmailTemplate(id: number) {
    return this.handleResponse(await this.api.post(`/email-template/${id}/reset`, {}));
  }
}

export default AdminService;
